import React, { useState, useEffect } from 'react';
import axios from '../api/axiosClient';
import { Card, Btn, StandardModal, Alert } from '../design-system';
import { inputStyle, textareaStyle, selectStyle } from '../design-system';
import { colors } from '../design-system/tokens';

const DOC_TYPE_LABELS = { formal: 'Formal Qualification', life_learned: 'Life-Learned Competency' };

const emptyMember = {
  first_name: '', last_name: '', job_title: '', email: '', phone: '', notes: '',
  formal_qualifications: [], life_competencies: []
};
const emptyQual = { title: '', institution: '', year: '' };
const emptyLife = { description: '', years_experience: '' };

export default function StaffCompetency() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('success');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyMember);
  const [saving, setSaving] = useState(false);

  const [selectedMember, setSelectedMember] = useState(null);
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadDocType, setUploadDocType] = useState('formal');
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const flash = (m, t = 'success') => { setMsg(m); setMsgType(t); setTimeout(() => setMsg(''), 4000); };

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/staff-competency/');
      setStaff(res.data);
    } catch { flash('Failed to load staff', 'error'); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setForm(emptyMember); setShowModal(true); };

  const openEdit = (member) => {
    setEditing(member.id);
    setForm({
      first_name: member.first_name,
      last_name: member.last_name,
      job_title: member.job_title || '',
      email: member.email || '',
      phone: member.phone || '',
      notes: member.notes || '',
      formal_qualifications: member.formal_qualifications || [],
      life_competencies: member.life_competencies || []
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`/staff-competency/${editing}`, form);
        flash('Staff member updated');
      } else {
        await axios.post('/staff-competency/', form);
        flash('Staff member added');
      }
      setShowModal(false);
      load();
    } catch (err) {
      flash(err.response?.data?.detail || 'Save failed', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this staff member and all their documents?')) return;
    try {
      await axios.delete(`/staff-competency/${id}`);
      flash('Staff member removed');
      if (selectedMember?.id === id) setSelectedMember(null);
      load();
    } catch { flash('Delete failed', 'error'); }
  };

  const addQual  = () => setForm(f => ({ ...f, formal_qualifications: [...f.formal_qualifications, { ...emptyQual }] }));
  const updateQual = (i, field, val) => setForm(f => { const q = [...f.formal_qualifications]; q[i] = { ...q[i], [field]: val }; return { ...f, formal_qualifications: q }; });
  const removeQual = (i) => setForm(f => ({ ...f, formal_qualifications: f.formal_qualifications.filter((_, j) => j !== i) }));

  const addLife  = () => setForm(f => ({ ...f, life_competencies: [...f.life_competencies, { ...emptyLife }] }));
  const updateLife = (i, field, val) => setForm(f => { const l = [...f.life_competencies]; l[i] = { ...l[i], [field]: val }; return { ...f, life_competencies: l }; });
  const removeLife = (i) => setForm(f => ({ ...f, life_competencies: f.life_competencies.filter((_, j) => j !== i) }));

  const reloadSelected = async (id) => {
    const res = await axios.get('/staff-competency/');
    setStaff(res.data);
    setSelectedMember(res.data.find(m => m.id === id) || null);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('document_type', uploadDocType);
    fd.append('document_title', uploadTitle);
    fd.append('file', uploadFile);
    try {
      await axios.post(`/staff-competency/${selectedMember.id}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      flash('Document uploaded');
      setUploadModal(false);
      setUploadTitle(''); setUploadFile(null);
      await reloadSelected(selectedMember.id);
    } catch (err) {
      flash(err.response?.data?.detail || 'Upload failed', 'error');
    } finally { setUploading(false); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`/staff-competency/documents/${docId}`);
      flash('Document deleted');
      await reloadSelected(selectedMember.id);
    } catch { flash('Delete failed', 'error'); }
  };

  const handleDownload = async (docId, fileName) => {
    try {
      const res = await axios.get(`/staff-competency/documents/${docId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.setAttribute('download', fileName);
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { flash('Download failed', 'error'); }
  };

  const labelStyle = { display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6, color: colors.textSecondary };
  const rowStyle   = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 };
  const sectionStyle = { marginBottom: 20 };

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Staff Competency Register</h1>
          <p style={{ margin: '4px 0 0', color: colors.textSecondary }}>
            Manage employee qualifications and upload proof of competency
          </p>
        </div>
        <Btn variant="primary" onClick={openCreate}>+ Add Staff Member</Btn>
      </div>

      {msg && <Alert type={msgType} message={msg} style={{ marginBottom: 16 }} />}

      <div style={{ display: 'grid', gridTemplateColumns: selectedMember ? '1fr 1.4fr' : '1fr', gap: 20 }}>

        {/* Staff list */}
        <div>
          {loading ? (
            <p style={{ color: colors.textMuted }}>Loading...</p>
          ) : staff.length === 0 ? (
            <Card padding="lg">
              <p style={{ textAlign: 'center', color: colors.textMuted }}>
                No staff members added yet. Click "+ Add Staff Member" to get started.
              </p>
            </Card>
          ) : staff.map(member => (
            <Card
              key={member.id}
              padding="md"
              style={{
                marginBottom: 12,
                cursor: 'pointer',
                border: selectedMember?.id === member.id
                  ? `2px solid ${colors.primary}`
                  : `2px solid ${colors.border}`,
                transition: 'border 0.15s'
              }}
              onClick={() => setSelectedMember(selectedMember?.id === member.id ? null : member)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: colors.textPrimary }}>
                    {member.first_name} {member.last_name}
                    {!member.is_active && (
                      <span style={{ marginLeft: 8, fontSize: 11, color: colors.textMuted }}>(Inactive)</span>
                    )}
                  </div>
                  {member.job_title && (
                    <div style={{ fontSize: 13, color: colors.textSecondary, marginTop: 2 }}>{member.job_title}</div>
                  )}
                  <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 12, color: colors.textMuted }}>
                    <span>{member.formal_qualifications?.length || 0} formal quals</span>
                    <span>{member.life_competencies?.length || 0} competencies</span>
                    <span>{member.documents?.length || 0} documents</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                  <Btn variant="secondary" size="sm" onClick={() => openEdit(member)}>Edit</Btn>
                  <Btn variant="danger" size="sm" onClick={() => handleDelete(member.id)}>Delete</Btn>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Detail panel */}
        {selectedMember && (
          <div>
            <Card padding="lg">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: 18, color: colors.textPrimary }}>
                    {selectedMember.first_name} {selectedMember.last_name}
                  </h2>
                  {selectedMember.job_title && (
                    <p style={{ margin: '2px 0 0', color: colors.textSecondary, fontSize: 13 }}>{selectedMember.job_title}</p>
                  )}
                </div>
                <Btn variant="primary" size="sm" onClick={() => setUploadModal(true)}>+ Upload Document</Btn>
              </div>

              {(selectedMember.email || selectedMember.phone) && (
                <div style={{ ...sectionStyle, fontSize: 13, color: colors.textSecondary }}>
                  {selectedMember.email && <span style={{ marginRight: 16 }}>✉ {selectedMember.email}</span>}
                  {selectedMember.phone && <span>📞 {selectedMember.phone}</span>}
                </div>
              )}

              {/* Formal qualifications */}
              <div style={sectionStyle}>
                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: colors.primary }}>
                  Formal Qualifications
                </h3>
                {(!selectedMember.formal_qualifications || selectedMember.formal_qualifications.length === 0) ? (
                  <p style={{ fontSize: 13, color: colors.textMuted }}>None recorded</p>
                ) : selectedMember.formal_qualifications.map((q, i) => (
                  <div key={i} style={{ padding: '8px 12px', background: colors.backgroundAlt, borderRadius: 6, marginBottom: 6, fontSize: 13 }}>
                    <strong>{q.title}</strong>
                    {q.institution && <span style={{ color: colors.textSecondary }}> — {q.institution}</span>}
                    {q.year && <span style={{ color: colors.textMuted }}> ({q.year})</span>}
                  </div>
                ))}
              </div>

              {/* Life-learned competencies */}
              <div style={sectionStyle}>
                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: colors.success }}>
                  Life-Learned Competencies
                </h3>
                {(!selectedMember.life_competencies || selectedMember.life_competencies.length === 0) ? (
                  <p style={{ fontSize: 13, color: colors.textMuted }}>None recorded</p>
                ) : selectedMember.life_competencies.map((l, i) => (
                  <div key={i} style={{ padding: '8px 12px', background: colors.backgroundAlt, borderRadius: 6, marginBottom: 6, fontSize: 13 }}>
                    <span>{l.description}</span>
                    {l.years_experience && <span style={{ color: colors.textMuted }}> — {l.years_experience} yrs exp</span>}
                  </div>
                ))}
              </div>

              {/* Documents */}
              <div>
                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: colors.textPrimary }}>
                  Proof Documents
                </h3>
                {(!selectedMember.documents || selectedMember.documents.length === 0) ? (
                  <p style={{ fontSize: 13, color: colors.textMuted }}>No documents uploaded yet</p>
                ) : ['formal', 'life_learned'].map(type => {
                  const docs = selectedMember.documents.filter(d => d.document_type === type);
                  if (docs.length === 0) return null;
                  return (
                    <div key={type} style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: type === 'formal' ? colors.primary : colors.success, marginBottom: 4 }}>
                        {DOC_TYPE_LABELS[type]}
                      </div>
                      {docs.map(doc => (
                        <div key={doc.id} style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '6px 10px', background: colors.backgroundAlt, borderRadius: 6,
                          marginBottom: 4, fontSize: 13
                        }}>
                          <span style={{ color: colors.textPrimary }}>
                            {doc.document_title}
                            <span style={{ color: colors.textMuted, fontSize: 11 }}> ({doc.file_name})</span>
                          </span>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <Btn variant="secondary" size="sm" onClick={() => handleDownload(doc.id, doc.file_name)}>Download</Btn>
                            <Btn variant="danger" size="sm" onClick={() => handleDeleteDoc(doc.id)}>Delete</Btn>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>

              {selectedMember.notes && (
                <div style={{ marginTop: 16, padding: '10px 12px', background: colors.backgroundAlt, borderRadius: 6, fontSize: 13 }}>
                  <strong>Notes:</strong> {selectedMember.notes}
                </div>
              )}
            </Card>
          </div>
        )}
      </div>

      {/* Add / Edit modal */}
      {showModal && (
        <StandardModal
          title={editing ? 'Edit Staff Member' : 'Add Staff Member'}
          onClose={() => setShowModal(false)}
          width="700px"
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowModal(false)}>Cancel</Btn>
              <Btn variant="primary" type="submit" form="staff-form" disabled={saving}>
                {saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Staff Member'}
              </Btn>
            </>
          }
        >
          <form id="staff-form" onSubmit={handleSave}>
            <div style={rowStyle}>
              <div>
                <label style={labelStyle}>First Name *</label>
                <input style={inputStyle} required value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
              </div>
              <div>
                <label style={labelStyle}>Last Name *</label>
                <input style={inputStyle} required value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Job Title</label>
              <input style={inputStyle} value={form.job_title} onChange={e => setForm(f => ({ ...f, job_title: e.target.value }))} />
            </div>
            <div style={rowStyle}>
              <div>
                <label style={labelStyle}>Email</label>
                <input style={inputStyle} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input style={inputStyle} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
            </div>

            {/* Formal qualifications */}
            <div style={{ ...sectionStyle, marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={{ ...labelStyle, margin: 0, color: colors.primary }}>Formal Qualifications</label>
                <Btn variant="outline" size="sm" type="button" onClick={addQual}>+ Add</Btn>
              </div>
              {form.formal_qualifications.map((q, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr auto', gap: 8, marginBottom: 8 }}>
                  <input style={inputStyle} placeholder="Qualification title" value={q.title} onChange={e => updateQual(i, 'title', e.target.value)} />
                  <input style={inputStyle} placeholder="Institution" value={q.institution} onChange={e => updateQual(i, 'institution', e.target.value)} />
                  <input style={inputStyle} placeholder="Year" value={q.year} onChange={e => updateQual(i, 'year', e.target.value)} />
                  <Btn variant="danger" size="sm" type="button" onClick={() => removeQual(i)}>✕</Btn>
                </div>
              ))}
              {form.formal_qualifications.length === 0 && (
                <p style={{ fontSize: 12, color: colors.textMuted }}>No formal qualifications added yet</p>
              )}
            </div>

            {/* Life-learned competencies */}
            <div style={sectionStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={{ ...labelStyle, margin: 0, color: colors.success }}>Life-Learned Competencies</label>
                <Btn variant="outline" size="sm" type="button" onClick={addLife}>+ Add</Btn>
              </div>
              {form.life_competencies.map((l, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '3fr 1fr auto', gap: 8, marginBottom: 8 }}>
                  <input style={inputStyle} placeholder="e.g. 10 years quality management experience" value={l.description} onChange={e => updateLife(i, 'description', e.target.value)} />
                  <input style={inputStyle} placeholder="Years" value={l.years_experience} onChange={e => updateLife(i, 'years_experience', e.target.value)} />
                  <Btn variant="danger" size="sm" type="button" onClick={() => removeLife(i)}>✕</Btn>
                </div>
              ))}
              {form.life_competencies.length === 0 && (
                <p style={{ fontSize: 12, color: colors.textMuted }}>No life-learned competencies added yet</p>
              )}
            </div>

            <div>
              <label style={labelStyle}>Notes</label>
              <textarea style={textareaStyle} rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </form>
        </StandardModal>
      )}

      {/* Upload document modal */}
      {uploadModal && (
        <StandardModal
          title="Upload Proof Document"
          onClose={() => { setUploadModal(false); setUploadTitle(''); setUploadFile(null); }}
          width="480px"
          footer={
            <>
              <Btn variant="secondary" onClick={() => { setUploadModal(false); setUploadTitle(''); setUploadFile(null); }}>Cancel</Btn>
              <Btn variant="primary" type="submit" form="upload-form" disabled={uploading || !uploadFile || !uploadTitle}>
                {uploading ? 'Uploading...' : 'Upload'}
              </Btn>
            </>
          }
        >
          <form id="upload-form" onSubmit={handleUpload}>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Document Type *</label>
              <select style={selectStyle} value={uploadDocType} onChange={e => setUploadDocType(e.target.value)}>
                <option value="formal">Formal Qualification</option>
                <option value="life_learned">Life-Learned Competency</option>
              </select>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Document Title *</label>
              <input style={inputStyle} required placeholder="e.g. BSc Engineering Certificate" value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>File *</label>
              <input type="file" required onChange={e => setUploadFile(e.target.files[0])}
                style={{ display: 'block', width: '100%', fontSize: 13 }} />
              <p style={{ fontSize: 11, color: colors.textMuted, marginTop: 4 }}>
                Accepted: PDF, Word, images, etc.
              </p>
            </div>
          </form>
        </StandardModal>
      )}
    </div>
  );
}
